import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Observable, Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { formatUpsTrackingActivityWhen } from "../../card-orders/card-order.util";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import type {
  PrintCardUpsShopRateQuote,
  PrintCardUpsShopRateQuotesResult,
  ShipPrintCardOrderUpsPayload,
  UpsDimensionUnit,
  UpsPackageShipmentPayload,
  UpsShopRatesDialogData,
  UpsWeightUnit,
} from "../print-card-orders.interface";
import { PrintCardOrdersService } from "../print-card-orders.service";

@Component({
  selector: "app-ups-shop-rates-dialog",
  templateUrl: "./ups-shop-rates-dialog.component.html",
  styleUrls: ["./ups-shop-rates-dialog.component.scss"],
  standalone: false,
})
export class UpsShopRatesDialogComponent implements OnInit, OnDestroy {
  packageWeight: number;
  readonly weightUnit: UpsWeightUnit;
  loading = false;
  result: PrintCardUpsShopRateQuotesResult | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: UpsShopRatesDialogData,
    private readonly dialogRef: MatDialogRef<UpsShopRatesDialogComponent>,
    private readonly ordersService: PrintCardOrdersService,
    private readonly toastr: ToastrTranslatedService,
  ) {
    this.packageWeight =
      Number(data.totalWeightKg) > 0 ? Number(data.totalWeightKg) : 0.5;
    this.weightUnit = data.weightUnit === "LBS" ? "LBS" : "KGS";
  }

  ngOnInit(): void {
    this.fetchRates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get quotes(): PrintCardUpsShopRateQuote[] {
    return this.result?.quotes ?? [];
  }

  formatUpsDateTime(
    date: string | null | undefined,
    time?: string | null,
  ): string {
    return (
      formatUpsTrackingActivityWhen(
        date?.trim() || null,
        time?.trim() || null,
      ) ??
      (date?.trim() || "—")
    );
  }

  formatAmount(value: number | string | null | undefined): string {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value.toFixed(2) : "—";
    }
    const trimmed = value?.trim();
    if (!trimmed) {
      return "—";
    }
    const n = Number(trimmed.replace(",", "."));
    return Number.isFinite(n) ? n.toFixed(2) : trimmed;
  }

  fetchRates(): void {
    const weight = Number(this.packageWeight);
    if (!Number.isFinite(weight) || weight <= 0) {
      this.toastr.error("printCardOrders.shopRates.invalidWeight");
      return;
    }

    const request$ = this.data.fetchRates
      ? this.data.fetchRates(weight, this.weightUnit)
      : this.buildOrderRatesRequest(weight);

    if (!request$) {
      this.toastr.error("printCardOrders.shopRates.failed");
      return;
    }

    this.loading = true;
    request$
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          this.result = result;
          if (Number.isFinite(result.totalWeight) && result.totalWeight > 0) {
            this.packageWeight = result.totalWeight;
          }
        },
        error: (error) => {
          this.result = null;
          this.toastr.apiError(error, "printCardOrders.shopRates.failed");
        },
      });
  }

  private buildOrderRatesRequest(
    weight: number,
  ): Observable<PrintCardUpsShopRateQuotesResult> | null {
    const orderId = this.data.orderId?.trim();
    const basePayload = this.data.payload;
    if (!orderId || !basePayload) {
      return null;
    }

    const dimensionUnit: UpsDimensionUnit =
      this.weightUnit === "LBS" ? "IN" : "CM";
    const packages = (basePayload.packages ?? []).map((pkg) => ({
      ...pkg,
    }));
    const currentTotal = packages.reduce(
      (sum, pkg) => sum + Number(pkg.packageWeight || 0),
      0,
    );
    const weightedPackages: UpsPackageShipmentPayload[] =
      packages.length === 0
        ? [
            {
              printerOrderNumbers: ["SHOP"],
              packageWeight: weight,
              weightUnit: this.weightUnit,
              packageLength: 30,
              packageWidth: 22,
              packageHeight: 10,
              dimensionUnit,
              packagingCode: basePayload.packagingCode || "02",
            },
          ]
        : packages.map((pkg) => {
            const packageWeight =
              packages.length === 1 || !(currentTotal > 0)
                ? Math.max(
                    0.01,
                    Math.round((weight / packages.length) * 100) / 100,
                  )
                : Math.max(
                    0.01,
                    Math.round(
                      weight *
                        (Number(pkg.packageWeight || 0) / currentTotal) *
                        100,
                    ) / 100,
                  );
            return {
              ...pkg,
              packageWeight,
              weightUnit: this.weightUnit,
              dimensionUnit: pkg.dimensionUnit || dimensionUnit,
            };
          });

    const payload: ShipPrintCardOrderUpsPayload = {
      ...basePayload,
      packages: weightedPackages,
      packageWeight: weight,
      weightUnit: this.weightUnit,
      dimensionUnit,
      pickup: { enabled: false },
      ratingRequestOption: "Ratetimeintransit",
    };

    return this.ordersService.getUpsShopRates(orderId, payload);
  }

  close(): void {
    this.dialogRef.close();
  }
}
